import React, { useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";

import ParticipantModal from "./ParticipantModal";
import PagedTable from "./PagedTable";
import {
  MAX_PARTICIPANTS,
  MIN_PARTICIPANTS,
  downloadSampleParticipantsCsv,
  exportParticipantsCsv,
  parseParticipantsCsv,
} from "./participantHelpers";

const HEADERS = ["No", "First Name", "Last Name", "Email Id", "Edit", "Delete"];
const SEARCH_KEYS = ["first_name", "last_name", "email_id"];

/** Participant editor from old/admin/participants.php. */
export default function ParticipantsTab({
  participants,
  isLoading,
  isSaving,
  onSaveParticipant,
  onDeleteParticipant,
  onImportCsv,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // old participants.php warns on load when the game cannot start yet.
  const warnedRef = useRef(false);
  useEffect(() => {
    if (isLoading || warnedRef.current) return;
    warnedRef.current = true;
    if (participants.length < MIN_PARTICIPANTS) {
      Swal.fire({ icon: "warning", text: `Please add a minimum of ${MIN_PARTICIPANTS} participants.` });
    }
  }, [isLoading, participants.length]);

  const existingEmails = useMemo(
    () => participants.map((p) => String(p.email_id ?? "").toLowerCase()),
    [participants]
  );

  const openAdd = () => {
    if (participants.length >= MAX_PARTICIPANTS) {
      Swal.fire({
        icon: "error",
        title: "Maximum Limit Reached!",
        text: `You can only add up to ${MAX_PARTICIPANTS} participants.`,
      });
      return;
    }
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const handleModalSubmit = async (participant) => {
    await onSaveParticipant({
      action: editing ? "edit" : "add",
      editId: editing?.id ?? null,
      participant,
    });
    closeModal();
  };

  const handleDelete = async (p) => {
    const result = await Swal.fire({
      title: "Are you sure you want to delete this item?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
    });
    if (result.isConfirmed) await onDeleteParticipant(p.id);
  };

  const handleCsvUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!/\.csv$/i.test(file.name)) {
      Swal.fire({ icon: "error", title: "Error", text: "Please use CSV File" });
      return;
    }

    let rows;
    try {
      rows = parseParticipantsCsv(await file.text());
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err?.message || "Failed to parse CSV." });
      return;
    }

    const confirm = await Swal.fire({
      title: "Upload CSV",
      text: `Participants will be overwritten with ${rows.length} participant(s) from the file.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Upload",
      cancelButtonText: "Cancel",
    });
    if (confirm.isConfirmed) await onImportCsv(rows);
  };

  return (
    <div className="themeupdate-panel-wide quiz-panel">
      <div className="quiz-page-header">
        <h2 className="quiz-page-title">Add Participants</h2>
        <div className="quiz-toolbar">
          <button type="button" className="quiz-toolbar-btn" onClick={openAdd} disabled={isSaving}>
            <i className="fa-solid fa-plus" aria-hidden /> Add Participants
          </button>
          <label className="quiz-toolbar-btn quiz-upload-label">
            <i className="fa-solid fa-upload" aria-hidden /> Upload CSV
            <input
              type="file"
              accept=".csv,text/csv"
              className="quiz-upload-input"
              onChange={handleCsvUpload}
              disabled={isSaving}
            />
          </label>
          <button
            type="button"
            className="quiz-toolbar-btn"
            onClick={() => exportParticipantsCsv(participants)}
            disabled={!participants.length}
          >
            <i className="fa-solid fa-file-export" aria-hidden /> Export CSV
          </button>
          <button type="button" className="quiz-toolbar-btn" onClick={downloadSampleParticipantsCsv}>
            <i className="fa-solid fa-download" aria-hidden /> Sample CSV
          </button>
        </div>
      </div>

      <p className="qa-char-limit-hint">
        * Add between {MIN_PARTICIPANTS} and {MAX_PARTICIPANTS} participants. Participants will be
        overwritten on CSV upload. Use only CSV files with the columns first_name, last_name,
        email_id (see the sample CSV). Email IDs must be unique.
      </p>

      <PagedTable
        headers={HEADERS}
        rows={participants}
        searchKeys={SEARCH_KEYS}
        isLoading={isLoading}
        emptyText="No participants yet. Click Add Participants or upload a CSV."
        renderCells={(p, no) => (
          <>
            <td>{no}</td>
            <td>{p.first_name}</td>
            <td>{p.last_name}</td>
            <td>{p.email_id}</td>
            <td>
              <button
                type="button"
                className="quiz-table-btn quiz-table-btn-edit"
                onClick={() => openEdit(p)}
              >
                <i className="fa-solid fa-pen-to-square" aria-hidden /> Edit
              </button>
            </td>
            <td>
              <button
                type="button"
                className="quiz-table-btn quiz-table-btn-delete"
                onClick={() => handleDelete(p)}
                disabled={isSaving}
              >
                <i className="fa-solid fa-trash" aria-hidden /> Delete
              </button>
            </td>
          </>
        )}
      />

      <ParticipantModal
        open={modalOpen}
        initial={editing}
        existingEmails={existingEmails}
        onClose={closeModal}
        onSubmit={handleModalSubmit}
        isSaving={isSaving}
      />
    </div>
  );
}
